import React, { Component } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './iLocalAGGrid.css';
import { withTranslation } from 'react-i18next';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CustomLoadingCellRenderer from './customLoadingCellRenderer';
import { getDefaultColDef } from './defaultColDef';
import { isActionsColumn, RowActionsMenuHost } from './actionsMenu';

export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 25];
export const SELECTION_COL_ID = 'ilocal-selection';

const SELECTION_COLUMN = {
  colId: SELECTION_COL_ID,
  headerName: '',
  valueGetter: () => '',
  checkboxSelection: true,
  headerCheckboxSelection: true,
  headerCheckboxSelectionFilteredOnly: true,
  width: 48,
  minWidth: 48,
  maxWidth: 48,
  flex: 0,
  pinned: 'left',
  lockPinned: true,
  lockPosition: true,
  suppressMovable: true,
  sortable: false,
  filter: false,
  floatingFilter: false,
  resizable: false,
  suppressMenu: true,
  suppressFiltersToolPanel: true,
  suppressColumnsToolPanel: true,
  menuTabs: [],
  cellClass: 'ilocal-selection-cell',
  headerClass: 'ilocal-selection-header',
};

class iLocalAGGrid extends Component {
  constructor(props) {
    super(props);

    const initialPageSize =
      props.paginationPageSize != null
        ? props.paginationPageSize
        : DEFAULT_PAGE_SIZE;

    this.state = {
      loadingCellRenderer: CustomLoadingCellRenderer,
      loadingCellRendererParams: {
        loadingMessage: 'Please Wait...',
      },
      pageSize: initialPageSize,
    };
    this.gridApi = null;
    this.gridColumnApi = null;
    this.wrapperRef = React.createRef();
    this._autoPinnedFilterCols = new Set();
    this._originalColumnState = null;
    this._pinningColumns = false;
    this._hasFirstData = false;
    this._stableColumnDefs = null;
    this._columnDefsSignature = null;
    this._stableDefaultColDef = null;
    this._wrappedGetRowId = null;
    this._wrappedGetRowIdSource = null;
    this._selectedIds = new Set();
    this._selectedIdsSignature = '';
    this._selectionSyncTimer = null;
  }

  /**
   * Parent reports rebuild columnDefs[] / getDefaultColDef() every setState.
   * Feeding new references into AgGridReact resets SSRM and leaves Please Wait...
   * even after the network response arrives. Keep stable refs when the column
   * identity (colId/field + header) has not changed.
   */
  getStableColumnDefs = (columnDefs, rowSelection) => {
    const next = Array.isArray(columnDefs) ? columnDefs : [];
    const withSelection =
      rowSelection === 'multiple' ? this.withSelectionColumn(next) : next;
    const signature = withSelection
      .map((c) => `${c.colId || c.field || ''}:${c.headerName || ''}`)
      .join('|');
    if (signature && signature === this._columnDefsSignature && this._stableColumnDefs) {
      return this._stableColumnDefs;
    }
    this._columnDefsSignature = signature;
    this._stableColumnDefs = withSelection;
    return withSelection;
  };

  withSelectionColumn = (columnDefs) => {
    const hasCheckbox = columnDefs.some(
      (col) =>
        col &&
        (col.colId === SELECTION_COL_ID ||
          col.checkboxSelection === true ||
          typeof col.checkboxSelection === 'function')
    );
    if (hasCheckbox) {
      return columnDefs;
    }
    return [SELECTION_COLUMN, ...columnDefs];
  };

  getStableDefaultColDef = (defaultColDef) => {
    if (defaultColDef) {
      if (this._stableDefaultColDef === defaultColDef) {
        return this._stableDefaultColDef;
      }
      // New object from getDefaultColDef() each render — reuse first resolved one.
      if (
        this._stableDefaultColDef &&
        defaultColDef.filter === this._stableDefaultColDef.filter &&
        defaultColDef.floatingFilter === this._stableDefaultColDef.floatingFilter
      ) {
        return this._stableDefaultColDef;
      }
      this._stableDefaultColDef = defaultColDef;
      return defaultColDef;
    }
    if (!this._stableDefaultColDef) {
      this._stableDefaultColDef = getDefaultColDef();
    }
    return this._stableDefaultColDef;
  };

  componentDidUpdate(prevProps) {
    // Ignore parent paginationPageSize churn after mount — reports often mutate
    // FilterObj.pageSize in didMount which would remount SSRM mid-fetch and
    // leave the grid stuck on Please Wait... Page size changes go through the
    // grid's own selector (handlePageSizeChange).
    if (
      !this.gridApi &&
      prevProps.paginationPageSize !== this.props.paginationPageSize &&
      this.props.paginationPageSize != null &&
      this.props.paginationPageSize !== this.state.pageSize
    ) {
      this.setState({ pageSize: this.props.paginationPageSize });
    }
  }

  getNoRowsMessage = () => {
    const { t, overlayNoRowsMessage } = this.props;
    if (overlayNoRowsMessage) {
      return overlayNoRowsMessage;
    }
    if (typeof t === 'function') {
      return t('Location.NoDataFound', {
        defaultValue: t('Assign.NoDataFound', { defaultValue: 'No Data Found' }),
      });
    }
    return 'No data found';
  };

  syncNoRowsOverlay = (api) => {
    if (!api || typeof api.getDisplayedRowCount !== 'function') {
      return;
    }
    const displayed = api.getDisplayedRowCount();
    if (displayed === 0) {
      api.showNoRowsOverlay();
    } else {
      api.hideOverlay();
    }
  };

  markFirstDataAndSyncPins = (api, columnApi) => {
    const wasFirst = !this._hasFirstData;
    this._hasFirstData = true;
    if (!wasFirst) return;
    const runPin = () => {
      this.syncFilteredColumnPinning(api, columnApi || this.gridColumnApi);
    };
    // Seed sets __ilocalSeedingFilter for ~250ms; pin after it clears so the
    // default date column still freezes like a user-applied filter.
    if (api && api.__ilocalSeedingFilter) {
      if (this._pinAfterSeedTimer) clearTimeout(this._pinAfterSeedTimer);
      this._pinAfterSeedTimer = setTimeout(runPin, 300);
      return;
    }
    if (this._pinFilterTimer) clearTimeout(this._pinFilterTimer);
    this._pinFilterTimer = setTimeout(runPin, 0);
  };

  handleModelUpdated = (event) => {
    const api = event && event.api;
    const rowModelType =
      this.props.rowModelType != null ? this.props.rowModelType : 'serverSide';
    if (rowModelType !== 'serverSide') {
      this.syncNoRowsOverlay(api);
    }
    if (
      !this._hasFirstData &&
      api &&
      typeof api.getDisplayedRowCount === 'function' &&
      api.getDisplayedRowCount() > 0
    ) {
      this.markFirstDataAndSyncPins(api, (event && event.columnApi) || this.gridColumnApi);
    }
    this.syncActiveFilterIndicators(api);
    this.scheduleSelectionSync(api);
    if (typeof this.props.onModelUpdated === 'function') {
      this.props.onModelUpdated(event);
    }
  };

  handleFirstDataRendered = (event) => {
    const api = event && event.api;
    this.markFirstDataAndSyncPins(api, (event && event.columnApi) || this.gridColumnApi);
    const rowModelType =
      this.props.rowModelType != null ? this.props.rowModelType : 'serverSide';
    if (rowModelType !== 'serverSide') {
      this.syncNoRowsOverlay(api);
    }
    this.syncActiveFilterIndicators(api);
    this.scheduleSelectionSync(api);
    if (typeof this.props.onFirstDataRendered === 'function') {
      this.props.onFirstDataRendered(event);
    }
  };

  /**
   * Highlight header + floating-filter cells for columns with an active filter.
   * AG Grid 29 does not expose api.getColumns(); use getFilterModel() + input values.
   */
  isFilterModelActive = (model) => {
    if (!model || typeof model !== 'object') return false;
    if (model.filter != null && String(model.filter).trim() !== '') return true;
    if (model.filterTo != null && String(model.filterTo).trim() !== '') return true;
    if (model.dateFrom || model.dateTo) return true;
    if (Array.isArray(model.values) && model.values.length > 0) return true;
    if (Array.isArray(model.conditions)) {
      return model.conditions.some((c) => this.isFilterModelActive(c));
    }
    if (model.condition1 && this.isFilterModelActive(model.condition1)) return true;
    if (model.condition2 && this.isFilterModelActive(model.condition2)) return true;
    return false;
  };

  syncActiveFilterIndicators = (api) => {
    const root = this.wrapperRef && this.wrapperRef.current;
    if (!root) return;

    const filterModel =
      api && typeof api.getFilterModel === 'function'
        ? api.getFilterModel() || {}
        : {};

    const activeIds = new Set();
    Object.keys(filterModel).forEach((colId) => {
      if (this.isFilterModelActive(filterModel[colId])) {
        activeIds.add(colId);
      }
    });

    // Floating filter row: also treat non-empty inputs as active (while typing /
    // before filterChanged fires).
    root.querySelectorAll('.ag-header-cell.ag-floating-filter').forEach((cell) => {
      const colId = cell.getAttribute('col-id');
      const input = cell.querySelector('input');
      const hasInputValue = !!(input && String(input.value || '').trim());
      const active = hasInputValue || (colId != null && activeIds.has(colId));
      cell.classList.toggle('ilocal-floating-filter-active', active);
      if (active && colId != null) {
        activeIds.add(colId);
      }
    });

    // Header label row
    root.querySelectorAll('.ag-header-cell:not(.ag-floating-filter)').forEach((cell) => {
      const colId = cell.getAttribute('col-id');
      const active = colId != null && activeIds.has(colId);
      cell.classList.toggle('ilocal-header-filtered', active);
    });
  };

  bindFilterInputListener = () => {
    const root = this.wrapperRef && this.wrapperRef.current;
    if (!root || this._filterInputBound) return;
    this._filterInputBound = true;
    this._onFilterInput = () => {
      this.syncActiveFilterIndicators(this.gridApi);
    };
    // Capture typing in floating filters immediately.
    root.addEventListener('input', this._onFilterInput, true);
    root.addEventListener('change', this._onFilterInput, true);
  };

  /**
   * Move actively filtered columns to the front and pin them left.
   * When a filter is cleared, unpin that column; when none remain, restore
   * the original column order/pin state from grid ready.
   */
  syncFilteredColumnPinning = (api, columnApi) => {
    if (this._pinningColumns) return;
    const colApi = columnApi || this.gridColumnApi;
    if (!api || !colApi) return;
    if (typeof colApi.setColumnsPinned !== 'function') return;

    const filterModel =
      typeof api.getFilterModel === 'function' ? api.getFilterModel() || {} : {};

    const activeIds = Object.keys(filterModel).filter((colId) =>
      this.isFilterModelActive(filterModel[colId])
    );

    // Snapshot original layout once, before we auto-pin anything.
    if (
      !this._originalColumnState &&
      typeof colApi.getColumnState === 'function'
    ) {
      this._originalColumnState = colApi.getColumnState();
    }

    const prevAuto = this._autoPinnedFilterCols;
    const toUnpin = [...prevAuto].filter(
      (id) => id !== SELECTION_COL_ID && !activeIds.includes(id)
    );

    this._pinningColumns = true;
    try {
      if (toUnpin.length) {
        colApi.setColumnsPinned(toUnpin, null);
      }

      if (activeIds.length) {
        colApi.setColumnsPinned(activeIds, 'left');
        if (typeof colApi.moveColumns === 'function') {
          const selectionCol =
            typeof colApi.getColumn === 'function'
              ? colApi.getColumn(SELECTION_COL_ID)
              : null;
          colApi.moveColumns(activeIds, selectionCol ? 1 : 0);
        }
        this._autoPinnedFilterCols = new Set(activeIds);
      } else {
        this._autoPinnedFilterCols = new Set();
        if (
          this._originalColumnState &&
          typeof colApi.applyColumnState === 'function'
        ) {
          colApi.applyColumnState({
            state: this._originalColumnState,
            applyOrder: true,
          });
        }
      }
    } finally {
      this._pinningColumns = false;
    }
  };

  handleFilterChanged = (event) => {
    const api = event && event.api;
    const columnApi = (event && event.columnApi) || this.gridColumnApi;
    this.syncActiveFilterIndicators(api);
    // Skip only while programmatically seeding the default date filter.
    // User apply/clear must pin/unpin immediately.
    const seeding = !!(api && api.__ilocalSeedingFilter);
    if (!seeding) {
      if (this._pinFilterTimer) clearTimeout(this._pinFilterTimer);
      this._pinFilterTimer = setTimeout(() => {
        this.syncFilteredColumnPinning(api, columnApi);
      }, 0);
    }
    if (typeof this.props.onFilterChanged === 'function') {
      this.props.onFilterChanged(event);
    }
  };

  handleFilterModified = (event) => {
    this.syncActiveFilterIndicators(event && event.api);
    if (typeof this.props.onFilterModified === 'function') {
      this.props.onFilterModified(event);
    }
  };

  handleGridReady = (params) => {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi || null;
    this._autoPinnedFilterCols = new Set();
    this._originalColumnState =
      this.gridColumnApi && typeof this.gridColumnApi.getColumnState === 'function'
        ? this.gridColumnApi.getColumnState()
        : null;
    this.bindFilterInputListener();
    this.syncActiveFilterIndicators(params.api);
    this._selectedIds = new Set();
    this._selectedIdsSignature = '';
    if (typeof this.props.onGridReady === 'function') {
      this.props.onGridReady(params);
    }
  };

  componentWillUnmount() {
    const root = this.wrapperRef && this.wrapperRef.current;
    if (root && this._onFilterInput) {
      root.removeEventListener('input', this._onFilterInput, true);
      root.removeEventListener('change', this._onFilterInput, true);
    }
    if (this._pinFilterTimer) clearTimeout(this._pinFilterTimer);
    if (this._pinAfterSeedTimer) clearTimeout(this._pinAfterSeedTimer);
    if (this._selectionSyncTimer) clearTimeout(this._selectionSyncTimer);
  }

  // Prevent ⋮ Actions clicks from bubbling into row/cell selection.
  handleCellMouseDown = (event) => {
    if (isActionsColumn(event.column) && event.event) {
      event.event.stopPropagation();
      if (typeof event.event.stopImmediatePropagation === 'function') {
        event.event.stopImmediatePropagation();
      }
    }
    if (typeof this.props.onCellMouseDown === 'function') {
      this.props.onCellMouseDown(event);
    }
  };

  handleCellClicked = (event) => {
    if (typeof this.props.onCellClicked === 'function') {
      this.props.onCellClicked(event);
    }
  };

  getSelectableRowId = (node) => {
    const data = node && node.data;
    if (!data) return null;
    if (data.id != null && data.id !== '') return data.id;
    if (data.ID != null && data.ID !== '') return data.ID;
    return null;
  };

  syncAccumulatedSelection = (api, { removeUnselectedLoaded = false } = {}) => {
    if (!api || typeof api.forEachNode !== 'function') return;
    api.forEachNode((node) => {
      const id = this.getSelectableRowId(node);
      if (id == null) return;
      if (node.isSelected()) {
        this._selectedIds.add(id);
      } else if (removeUnselectedLoaded) {
        this._selectedIds.delete(id);
      }
    });
  };

  scheduleSelectionSync = (api) => {
    if (!api) return;
    if (this._selectionSyncTimer) clearTimeout(this._selectionSyncTimer);
    this._selectionSyncTimer = setTimeout(() => {
      this.syncAccumulatedSelection(api, { removeUnselectedLoaded: false });
      this.emitSelectionToParent({ api });
    }, 0);
  };

  emitSelectionToParent = (event) => {
    const api = (event && event.api) || this.gridApi;
    if (!api) return;
    if (
      typeof this.props.onRowSelected !== 'function' &&
      typeof this.props.onSelectionChanged !== 'function'
    ) {
      return;
    }

    const selectedNodes = [...this._selectedIds].map((id) => ({
      data: { id, ID: id },
      isSelected: () => true,
    }));
    const signature = selectedNodes.map((n) => n.data.id).join('|');
    if (signature === this._selectedIdsSignature) return;
    this._selectedIdsSignature = signature;

    const forwarded = {
      ...(event || {}),
      api: {
        getSelectedNodes: () => selectedNodes,
      },
    };
    if (typeof this.props.onRowSelected === 'function') {
      this.props.onRowSelected(forwarded);
    }
    if (typeof this.props.onSelectionChanged === 'function') {
      this.props.onSelectionChanged(forwarded);
    }
  };

  // Checkbox / header "select all" updates come through selectionChanged.
  // List pages listen to onRowSelected, so forward both from one place.
  handleSelectionChanged = (event) => {
    const source = event && event.source;
    const isUserSelection =
      source === 'checkboxSelected' ||
      source === 'uiSelectAll' ||
      source === 'uiSelectAllFiltered' ||
      source === 'uiSelectAllCurrentPage';
    this.syncAccumulatedSelection(event && event.api, {
      removeUnselectedLoaded: isUserSelection,
    });
    this.emitSelectionToParent(event);
  };

  handlePaginationChanged = (event) => {
    this.scheduleSelectionSync(event && event.api);
    if (typeof this.props.onPaginationChanged === 'function') {
      this.props.onPaginationChanged(event);
    }
  };

  refreshAfterPageSizeChange = (api) => {
    if (!api) return;
    if (typeof api.refreshServerSideStore === 'function') {
      api.refreshServerSideStore({ purge: true });
    } else if (typeof api.refreshServerSide === 'function') {
      api.refreshServerSide({ purge: true });
    }
  };

  handlePageSizeChange = (event) => {
    const nextSize = Number(event.target.value);
    if (!nextSize || nextSize === this.state.pageSize) {
      return;
    }

    this.setState({ pageSize: nextSize }, () => {
      const api = this.gridApi;
      if (!api) return;

      if (typeof api.paginationSetPageSize === 'function') {
        api.paginationSetPageSize(nextSize);
      }
      if (typeof api.setCacheBlockSize === 'function') {
        api.setCacheBlockSize(nextSize);
      }
      if (typeof api.paginationGoToFirstPage === 'function') {
        api.paginationGoToFirstPage();
      }

      const rowModelType =
        this.props.rowModelType != null ? this.props.rowModelType : 'serverSide';
      if (rowModelType === 'serverSide') {
        this.refreshAfterPageSizeChange(api);
      }

      if (typeof this.props.onPaginationChanged === 'function') {
        this.props.onPaginationChanged({ api });
      }
    });
  };

  render() {
    const {
      gridHeight,
      columnDefs,
      rowData,
      sideBar = false,
      suppressMenuHide = true,
      rowModelType = 'serverSide',
      enableSorting = true,
      enableFilter = true,
      floatingFilter = true,
      enableColResize = true,
      rowSelection,
      defaultColDef,
      cacheBlockSize,
      maxBlocksInCache,
      rowBuffer,
      pagination = true,
      paginationPageSizeSelector = DEFAULT_PAGE_SIZE_OPTIONS,
      domLayout = 'autoHeight',
      rowHeight = 45,
      getRowId,
      getContextMenuItems,
      allowContextMenuWithControlKey = true,
      overlayNoRowsTemplate,
      className,
      style,
      t,
    } = this.props;
    const { loadingCellRendererParams, loadingCellRenderer, pageSize } = this.state;

    const resolvedDefaultColDef = this.getStableDefaultColDef(defaultColDef);
    const resolvedColumnDefs = this.getStableColumnDefs(columnDefs, rowSelection);
    const noRowsMessage = this.getNoRowsMessage();
    const resolvedNoRowsTemplate =
      overlayNoRowsTemplate ||
      `<span class="ag-overlay-no-rows-center" style="padding: 24px; color: rgba(0,0,0,0.54); font-size: 14px;">${String(
        noRowsMessage
      )
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')}</span>`;

    const pageSizeOptions =
      Array.isArray(paginationPageSizeSelector) && paginationPageSizeSelector.length
        ? paginationPageSizeSelector
        : DEFAULT_PAGE_SIZE_OPTIONS;

    const resolvedCacheBlockSize =
      cacheBlockSize != null ? cacheBlockSize : pageSize;

    // Datasource stamps __ssrmId for uniqueness. Prefer it so parent getRowId
    // helpers that fall back to short JSON / empty txn ids cannot collide and
    // leave SSRM stuck on Please Wait... (AG Grid rejects duplicate row ids).
    let resolvedGetRowId = getRowId;
    if (typeof getRowId === 'function') {
      if (this._wrappedGetRowIdSource !== getRowId) {
        this._wrappedGetRowIdSource = getRowId;
        this._wrappedGetRowId = (params) => {
          const d = params && params.data;
          if (d && d.__ssrmId != null && d.__ssrmId !== '') {
            return String(d.__ssrmId);
          }
          return getRowId(params);
        };
      }
      resolvedGetRowId = this._wrappedGetRowId;
    } else {
      this._wrappedGetRowIdSource = null;
      this._wrappedGetRowId = null;
    }

    const wrapperStyle = {
      boxSizing: 'border-box',
      width: '100%',
      position: 'relative',
      minHeight: gridHeight != null ? undefined : 180,
      ...(gridHeight != null ? { height: gridHeight } : {}),
      ...(style || {}),
    };

    const pageSizeLabel =
      (typeof t === 'function' &&
        t('Global.Label.PageSize', { defaultValue: 'Page Size' })) ||
      'Page Size';

    // maxBlocksInCache: 0 purges every loaded block (esp. with autoHeight).
    // Only pass a positive limit; omit otherwise so AG Grid keeps an unlimited cache.
    const gridProps = {
      rowBuffer,
      rowModelType,
      cacheBlockSize: resolvedCacheBlockSize,
      columnDefs: resolvedColumnDefs,
      rowData,
      sideBar,
      suppressMenuHide,
      onGridReady: this.handleGridReady,
      loadingCellRenderer,
      loadingCellRendererParams,
      enableSorting,
      enableFilter,
      floatingFilter,
      enableColResize,
      rowSelection,
      suppressRowClickSelection: rowSelection === 'multiple',
      rowMultiSelectWithClick: false,
      defaultColDef: resolvedDefaultColDef,
      onRowSelected: this.handleSelectionChanged,
      onSelectionChanged: this.handleSelectionChanged,
      onCellMouseDown: this.handleCellMouseDown,
      onCellClicked: this.handleCellClicked,
      onRowClicked: this.props.onRowClicked,
      pagination,
      paginationPageSize: pageSize,
      onPaginationChanged: this.handlePaginationChanged,
      domLayout,
      rowHeight,
      getRowId: resolvedGetRowId,
      onFirstDataRendered: this.handleFirstDataRendered,
      onModelUpdated: this.handleModelUpdated,
      onFilterChanged: this.handleFilterChanged,
      onFilterModified: this.handleFilterModified,
      getContextMenuItems,
      allowContextMenuWithControlKey,
      overlayNoRowsTemplate: resolvedNoRowsTemplate,
      localeText: {
        noRowsToShow: noRowsMessage,
      },
    };
    if (maxBlocksInCache != null && maxBlocksInCache > 0) {
      gridProps.maxBlocksInCache = maxBlocksInCache;
    }

    return (
      <div
        ref={this.wrapperRef}
        className={className || 'ag-theme-alpine'}
        style={wrapperStyle}
      >
        <AgGridReact {...gridProps} />
        {pagination ? (
          <div
            className="ilocal-ag-page-size"
            style={{
              position: 'absolute',
              left: 8,
              bottom: 6,
              zIndex: 2,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 32,
              fontSize: 13,
              color: '#181d1f',
              background: 'transparent',
            }}
          >
            <label htmlFor="ilocal-ag-page-size-select" style={{ margin: 0 }}>
              {pageSizeLabel}
            </label>
            <Select
              id="ilocal-ag-page-size-select"
              value={pageSize}
              onChange={this.handlePageSizeChange}
              variant="standard"
              disableUnderline
              inputProps={{ 'aria-label': pageSizeLabel }}
              MenuProps={{
                disableScrollLock: true,
                anchorOrigin: { vertical: 'top', horizontal: 'left' },
                transformOrigin: { vertical: 'bottom', horizontal: 'left' },
              }}
              sx={{
                fontSize: 13,
                height: 26,
                minWidth: 56,
                '& .MuiSelect-select': {
                  paddingTop: 0,
                  paddingBottom: 0,
                  paddingLeft: '8px',
                  paddingRight: '24px !important',
                  boxSizing: 'border-box',
                  border: '1px solid #babfc7',
                  borderRadius: '3px',
                  backgroundColor: '#fff',
                  height: 26,
                  lineHeight: '24px',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            >
              {pageSizeOptions.map((size) => (
                <MenuItem key={size} value={size} dense>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </div>
        ) : null}
        <RowActionsMenuHost />
      </div>
    );
  }
}

export default withTranslation()(iLocalAGGrid);

export { getDefaultColDef } from './defaultColDef';
export { createServerSideDatasource } from './createServerSideDatasource';
export {
  buildSearchObject,
  buildManageBoxesSearchObject,
  buildManageAccountsSearchObject,
  foldFiltersToSearchText,
  formatAddressField,
  applySortModel,
  DATE_COLUMN_FILTER_PARAMS,
  withDateColumnFilter,
  buildDateRangeFilterModel,
  seedReportDateFilter,
  attachReportServerSideDatasource,
  applyDateRangeFromFilterModel,
  restoreDefaultReportDates,
  applyReportFilterModel,
} from './filterUtils';
export { getAddressColumns } from './addressColumns';
export {
  ActionsMenuCellRenderer,
  getActionsColumn,
  withClickableCell,
  getReportViewActionsColumn,
  resolveGridRow,
  openAgGridContextMenu,
  isActionsColumn,
  actionMenuItem,
  createMenuPositionEvent,
  showRowActionsMenu,
  RowActionsMenuHost,
} from './actionsMenu';



// WEBPACK FOOTER //
// ./src/components/grid/iLocalAGGrid.js